/* Verasah — global theme behaviours (deferred) */

/* Chain add-on products (handle convention: {style}-chain, e.g.
   classic-link-chain) are bundled with a pendant the same way Express/
   Priority Crafting upgrades are: no independent remove button, and
   removed automatically when their parent pendant is removed. Shared
   across the cart singleton and both renderers below. */
function isChainHandle(h) { return h && h.slice(-6) === '-chain'; }

/* Shared by the cart drawer AND the cart page renderers: build the
   expandable customisation details panel. Must stay top-level -- it was
   inside the drawer block, so the cart page crashed calling it. */
function renderDetails(props) {
  if (!props) return '';
  var skip = { 'Estimated Delivery': 1, 'Completion Date': 1 };
  var rows = [];
  Object.keys(props).forEach(function (k) {
    if (skip[k] || k.charAt(0) === '_') return;
    var v = String(props[k] || '').trim();
    if (!v || v.toLowerCase() === 'none') return;
    rows.push({ k: k, v: v });
  });
  if (!rows.length) return '';
  var uid = 'dtl-' + Math.random().toString(36).slice(2, 7);
  return '<button class="cart-details__toggle" data-details-toggle="' + uid + '" aria-expanded="false">' +
    '<svg class="cart-details__chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>' +
    'View customisation</button>' +
    '<div class="cart-details__panel" id="' + uid + '" hidden>' +
    rows.map(function (r) {
      return '<div class="cart-details__row"><span class="cart-details__key">' + r.k + '</span><span class="cart-details__val">' + r.v + '</span></div>';
    }).join('') +
    '</div>';
}


/* ═══════════════════════════════════════════════════════════════
   CART SINGLETON — single source of truth for cart state.
   Any file can call:
     VerasahCart.refresh()          — re-fetch and broadcast
     VerasahCart.change(key, qty)   — change qty then broadcast
     VerasahCart.add(items)         — add items then broadcast
   Any file can listen:
     document.addEventListener('verasah:cart', function(e) {
       var cart = e.detail;  // full Shopify cart object
     });
   ═══════════════════════════════════════════════════════════════ */
window.VerasahCart = (function () {

  function broadcast(cart) {
    document.dispatchEvent(new CustomEvent('verasah:cart', { detail: cart }));
  }

  function refresh() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { broadcast(cart); return cart; });
  }

  function change(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      /* If removing a product, also remove its linked crafting upgrade
         and/or chain add-on — both are bundled, never independently kept.
         Match by _for_uid first (precise -- the exact line just removed),
         falling back to product title only when no uid was captured (e.g.
         a legacy cart line added before _uid existed). Title-only matching
         is genuinely ambiguous once the same product is in the cart twice
         under different variants -- both lines share one title, so it could
         match the wrong line's linked item, or both. */
      if (qty === 0) {
        var linked = cart.items.filter(function (u) {
          if (u.handle.indexOf('crafting-upgrade') === -1 && !isChainHandle(u.handle)) return false;
          var removedUid   = window._srbRemovedUid || '';
          var removedTitle = window._srbRemovedTitle || '';
          var forUid   = u.properties && u.properties['_for_uid'];
          var forTitle = u.properties && u.properties['_for'];
          if (removedUid && forUid) return forUid === removedUid;
          return forTitle && forTitle === removedTitle;
        });
        if (linked.length === 0) return refresh();
        return Promise.all(linked.map(function (u) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: u.key, quantity: 0 })
          });
        })).then(refresh);
      }
      broadcast(cart);
      return cart;
    });
  }

  function add(items) {
    /* items: array of { id, quantity, properties } */
    return items.reduce(function (p, item) {
      return p.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(item)
        }).then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw e; });
          return r.json();
        });
      });
    }, Promise.resolve()).then(refresh);
  }

  return { refresh: refresh, change: change, add: add, broadcast: broadcast };
})();

/* ═══════════════════════════════════════════════════════════════
   CART DRAWER RENDERER
   Listens to verasah:cart and re-renders the drawer every time.
   ═══════════════════════════════════════════════════════════════ */
(function () {

  function isUpg(h) { return h && h.indexOf('crafting-upgrade') !== -1; }

  var STAR_SVG = '<svg width="8" height="8" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>';

  function renderUpg(u) {
    var isExpress = u.handle.indexOf('express') !== -1;
    var label = isExpress ? 'Express Crafting' : 'Priority Crafting';
    var price = window.vMoney(u.final_line_price);
    return '<div class="cart-item--upgrade" data-cart-item="' + u.key + '">' +
      '<div class="upg-pill">' +
      '<div class="upg-pill__gem">' + STAR_SVG + '</div>' +
      '<span class="upg-pill__name">' + label + '</span>' +
      '<div class="upg-pill__sep"></div>' +
      '<span class="upg-pill__price">' + price + '</span>' +
      '</div></div>';
  }

  /* Chain add-on — same "bundled, no remove button" treatment as
     renderUpg, but shown as a full item row (it's a real physical product
     with its own length/metal, not a short service upsell pill). */
  function renderChain(c) {
    var meta = (c.variant_title && c.variant_title !== 'Default Title')
      ? '<p class="cart-item__meta">' + c.variant_title + '</p>' : '';
    var price = '<p class="cart-item__price">' + window.vMoney(c.final_line_price) + '</p>';
    return '<div class="cart-item cart-item--bundled" data-cart-item="' + c.key + '">' +
      '<div class="cart-item__info"><p class="cart-item__title">' + (c.product_title || c.title) + '</p>' +
      meta +
      '<p class="cart-item__bundled-note">Included with your pendant</p>' +
      '<div class="cart-item__footer-row">' + price + '</div>' +
      '</div></div>';
  }

  function renderDrawer(cart) {
    /* Count bubble */
    var countEl = document.querySelector('[data-cart-count]');
    if (countEl) {
      var displayCount = cart.items.reduce(function (n, i) {
        return (isUpg(i.handle) || isChainHandle(i.handle)) ? n : n + i.quantity;
      }, 0);
      countEl.textContent = displayCount;
      countEl.hidden = displayCount === 0;
    }

    /* Subtotal */
    var subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = window.vMoney(cart.total_price);

    /* Items */
    var cartItemsEl = document.querySelector('[data-cart-items]');
    if (!cartItemsEl) return;

    if (cart.item_count === 0) {
      cartItemsEl.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      var emptyBtn = document.querySelector('[data-empty-cart]');
      if (emptyBtn) emptyBtn.remove();
      return;
    }

    var mainItems  = cart.items.filter(function (i) { return !isUpg(i.handle) && !isChainHandle(i.handle); });
    var upgItems   = cart.items.filter(function (i) { return  isUpg(i.handle); });
    var chainItems = cart.items.filter(function (i) { return  isChainHandle(i.handle); });
    var matched   = {};

    var html = mainItems.map(function (m) {
      var img = (m.featured_image && m.featured_image.url)
        ? '<a href="/products/' + m.handle + '"><img src="' + m.featured_image.url + '" class="cart-item__image" alt="' + (m.title || '') + '" width="80" height="80"></a>' : '';
      var meta = (m.variant_title && m.variant_title !== 'Default Title')
        ? '<p class="cart-item__meta">' + m.variant_title + '</p>' : '';
      var delivery = '';
      if (m.properties && m.properties['Completion Date']) {
        delivery = '<p class="cart-item__delivery"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>Completion: <em>' + m.properties['Completion Date'] + '</em></p>';
      } else if (m.properties && m.properties['Estimated Delivery']) {
        delivery = '<p class="cart-item__delivery"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>Est. delivery: <em>' + m.properties['Estimated Delivery'] + '</em></p>';
      }
      var mUidAttr = (m.properties && m.properties['_uid']) || '';
      var remove = '<button class="cart-item__remove" data-remove-item="' + m.key + '" data-remove-title="' + (m.product_title || '') + '" data-remove-uid="' + mUidAttr + '" aria-label="Remove item">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>' +
        '</button>';
      var price = '<p class="cart-item__price">' + window.vMoney(m.final_line_price) + '</p>';
      var details = renderDetails(m.properties);
      var out = '<div class="cart-item" data-cart-item="' + m.key + '">' +
        img + '<div class="cart-item__info"><p class="cart-item__title">' + (m.product_title || m.title) + '</p>' +
        meta + delivery + details +
        '<div class="cart-item__footer-row">' + price + remove + '</div>' +
        '</div></div>';
      /* Slot matching upgrade immediately below — match by _for_uid first (precise),
         fall back to product title for legacy items */
      upgItems.forEach(function (u) {
        if (matched[u.key]) return;
        var forUid   = u.properties && u.properties['_for_uid'];
        var forTitle = u.properties && u.properties['_for'];
        var mUid     = m.properties && m.properties['_uid'];
        var isMatch  = forUid ? (mUid && mUid === forUid) : (forTitle === m.product_title);
        if (isMatch) { out += renderUpg(u); matched[u.key] = true; }
      });
      chainItems.forEach(function (c) {
        if (matched[c.key]) return;
        var forUid   = c.properties && c.properties['_for_uid'];
        var forTitle = c.properties && c.properties['_for'];
        var mUid     = m.properties && m.properties['_uid'];
        var isMatch  = forUid ? (mUid && mUid === forUid) : (forTitle === m.product_title);
        if (isMatch) { out += renderChain(c); matched[c.key] = true; }
      });
      return out;
    }).join('');

    /* Unmatched upgrades/chains (legacy) at bottom */
    upgItems.forEach(function (u) { if (!matched[u.key]) html += renderUpg(u); });
    chainItems.forEach(function (c) { if (!matched[c.key]) html += renderChain(c); });

    cartItemsEl.innerHTML = html;
  }

  /* Expose so ring builder can call it too */
  window.renderCartDrawer = renderDrawer;

  /* Listen to central cart event */
  document.addEventListener('verasah:cart', function (e) {
    renderDrawer(e.detail);
  });

})();

/* ═══════════════════════════════════════════════════════════════
   CART PAGE RENDERER
   If the full cart page is open (/cart), also re-render it
   when the cart changes — keeps it in sync with the drawer.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  var cartPageItems = document.querySelector('.cart-page__items');
  if (!cartPageItems) return; /* not on cart page */

  function isUpg(h) { return h && h.indexOf('crafting-upgrade') !== -1; }

  function renderCartPage(cart) {
    var subtotalEl = document.querySelector('[data-page-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = window.vMoney(cart.total_price);

    if (cart.item_count === 0) { window.location.reload(); return; }

    var mainItems  = cart.items.filter(function (i) { return !isUpg(i.handle) && !isChainHandle(i.handle); });
    var upgItems   = cart.items.filter(function (i) { return  isUpg(i.handle); });
    var chainItems = cart.items.filter(function (i) { return  isChainHandle(i.handle); });
    var matched   = {};

    function upgradeLabel(u) {
      return u.handle.indexOf('express') !== -1 ? 'Express Crafting' : 'Priority Crafting';
    }

    function renderUpgRow(u, parentTitle) {
      var isExpress = u.handle.indexOf('express') !== -1;
      var label = isExpress ? 'Express Crafting' : 'Priority Crafting';
      var price = window.vMoney(u.final_line_price);
      var starSvg = '<svg width="9" height="9" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>';
      return '<div class="cart-page__item--upgrade" data-upgrade-for="' + (parentTitle || '') + '">' +
        '<div class="cart-page__upg-pill-wrap">' +
        '<div class="upg-pill upg-pill--lg">' +
        '<div class="upg-pill__gem upg-pill__gem--lg">' + starSvg + '</div>' +
        '<span class="upg-pill__name">' + label + '</span>' +
        '<div class="upg-pill__sep"></div>' +
        '<span class="upg-pill__price">' + price + '</span>' +
        '</div></div></div>';
    }

    /* Chain add-on row — bundled, no remove button, same treatment as the
       drawer's renderChain. */
    function renderChainRow(c) {
      var meta = (c.variant_title && c.variant_title !== 'Default Title')
        ? '<p class="cart-page__item-meta">' + c.variant_title + '</p>' : '';
      var price = '<p class="cart-page__item-price">' + window.vMoney(c.final_line_price) + '</p>';
      return '<div class="cart-page__item cart-page__item--bundled">' +
        '<div class="cart-page__item-info">' +
        '<p class="cart-page__item-title">' + (c.product_title || c.title) + '</p>' +
        meta +
        '<p class="cart-page__item-bundled-note">Included with your pendant</p>' +
        '<div class="cart-page__item-footer">' + price + '</div>' +
        '</div></div>';
    }

    var html = mainItems.map(function (m) {
      var img = (m.image && m.image.url)
        ? '<a href="/products/' + m.handle + '" class="cart-page__item-image-link"><img src="' + m.image.url + '" class="cart-page__item-image" alt="' + (m.title || '') + '" width="100" height="100"></a>'
        : (m.featured_image && m.featured_image.url
          ? '<a href="/products/' + m.handle + '" class="cart-page__item-image-link"><img src="' + m.featured_image.url + '" class="cart-page__item-image" alt="' + (m.title || '') + '" width="100" height="100"></a>'
          : '');
      var meta = (m.variant_title && m.variant_title !== 'Default Title')
        ? '<p class="cart-page__item-meta">' + m.variant_title + '</p>' : '';
      var delivery = '';
      if (m.properties && m.properties['Completion Date']) {
        delivery = '<p class="cart-page__item-delivery"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>Completion: <em>' + m.properties['Completion Date'] + '</em></p>';
      } else if (m.properties && m.properties['Estimated Delivery']) {
        delivery = '<p class="cart-page__item-delivery"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>Est. delivery: <em>' + m.properties['Estimated Delivery'] + '</em></p>';
      }
      var price = '<p class="cart-page__item-price">' + window.vMoney(m.final_line_price) + '</p>';
      var mUidAttrPage = (m.properties && m.properties['_uid']) || '';
      var remove = '<button class="cart-page__remove" data-remove-item="' + m.key + '" data-remove-title="' + (m.product_title || '') + '" data-remove-uid="' + mUidAttrPage + '" aria-label="Remove item">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg> Remove' +
        '</button>';
      var details = renderDetails(m.properties);
      var out = '<div class="cart-page__item">' +
        img + '<div class="cart-page__item-info">' +
        '<a href="/products/' + m.handle + '" class="cart-page__item-title">' + (m.product_title || m.title) + '</a>' +
        meta + delivery + details +
        '<div class="cart-page__item-footer">' + price + remove + '</div>' +
        '</div></div>';
      /* Slot matching upgrade below — match by _for_uid first, title as fallback */
      upgItems.forEach(function (u) {
        if (matched[u.key]) return;
        var forUid   = u.properties && u.properties['_for_uid'];
        var forTitle = u.properties && u.properties['_for'];
        var mUid     = m.properties && m.properties['_uid'];
        var isMatch  = forUid ? (mUid && mUid === forUid) : (forTitle === m.product_title);
        if (isMatch) { out += renderUpgRow(u, m.product_title); matched[u.key] = true; }
      });
      chainItems.forEach(function (c) {
        if (matched[c.key]) return;
        var forUid   = c.properties && c.properties['_for_uid'];
        var forTitle = c.properties && c.properties['_for'];
        var mUid     = m.properties && m.properties['_uid'];
        var isMatch  = forUid ? (mUid && mUid === forUid) : (forTitle === m.product_title);
        if (isMatch) { out += renderChainRow(c); matched[c.key] = true; }
      });
      return out;
    }).join('');

    /* Unmatched upgrades/chains at bottom */
    upgItems.forEach(function (u) { if (!matched[u.key]) html += renderUpgRow(u, ''); });
    chainItems.forEach(function (c) { if (!matched[c.key]) html += renderChainRow(c); });

    cartPageItems.innerHTML = html;
  }

  /* Listen to central cart event — re-render cart page too */
  document.addEventListener('verasah:cart', function (e) {
    renderCartPage(e.detail);
  });

  /* Remove is handled by the global [data-remove-item] listener in DOMContentLoaded */

})();

/* ═══════════════════════════════════════════════════════════════
   DRAWER UI — open/close, qty changes, empty cart
   ═══════════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', function () {

  /* ── Sticky header ── */
  var header = document.querySelector('[data-header]');
  if (header && header.classList.contains('site-header--sticky-scroll')) {
    var sentinel = document.createElement('div');
    document.body.prepend(sentinel);
    new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }).observe(sentinel);
  }

  /* ── Transparent nav ── */
  var navWrapper = document.querySelector('[data-site-nav]');
  if (navWrapper && navWrapper.hasAttribute('data-transparent')) {
    var hero = document.querySelector('.video-hero');
    if (hero) {
      new IntersectionObserver(function (entries) {
        navWrapper.classList.toggle('is-scrolled', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(hero);
    }
  }

  /* ── Cart drawer open/close ── */
  var cartDrawer = document.getElementById('cart-drawer');
  function openCart()  { if (cartDrawer) cartDrawer.classList.add('is-open'); }
  function closeCart() { if (cartDrawer) cartDrawer.classList.remove('is-open'); }

  document.querySelectorAll('[data-cart-open]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      /* Open drawer instantly, then fetch fresh data in background */
      openCart();
      VerasahCart.refresh();
    });
  });
  document.querySelectorAll('[data-cart-close]').forEach(function (btn) {
    btn.addEventListener('click', closeCart);
  });

  /* ── Search modal ── */
  var searchModal = document.getElementById('search-modal');
  document.querySelectorAll('[data-search-open]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (searchModal) {
        searchModal.removeAttribute('hidden');
        var inp = searchModal.querySelector('.search-modal__input');
        if (inp) inp.focus();
      }
    });
  });
  document.querySelectorAll('[data-search-close]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (searchModal) searchModal.setAttribute('hidden', '');
    });
  });

  /* ── Empty cart ── */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-empty-cart]')) return;
    if (!confirm('Remove all items from your cart?')) return;
    fetch('/cart/clear.js', { method: 'POST' })
      .then(function (r) { return r.json(); })
      .then(function () { VerasahCart.refresh(); });
  });

  /* ── Remove item (drawer + cart page) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-remove-item]');
    if (!btn) return;
    var key   = btn.dataset.removeItem;
    var title = btn.dataset.removeTitle || '';
    var uid   = btn.dataset.removeUid || '';
    window._srbRemovedTitle = title;
    window._srbRemovedUid = uid;
    VerasahCart.change(key, 0);
  });

  /* ── Expandable customisation details toggle ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-details-toggle]');
    if (!btn) return;
    var panel = document.getElementById(btn.dataset.detailsToggle);
    if (!panel) return;
    var open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;
    btn.classList.toggle('is-open', !open);
  });

});

/* ═══════════════════════════════════════════════════════════════
   READY-TO-SHIP RE-CHECK (2026-10-28)
   A cart line carries "Estimated Delivery" when it was added while stock
   covered it. If the piece has since sold, that promise no longer holds, so
   whenever the cart changes (drawer / cart page) and when Checkout is
   clicked, live stock is re-read and any line it no longer covers loses the
   date (it becomes made to order). Everything else on the line -- _uid,
   ring size, engraving, quantity, upgrades -- is kept. Fails safe: if stock
   can't be read the line is left alone and checkout always continues.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  var DELIVERY = 'Estimated Delivery';

  function liveStock(handle) {
    /* No Accept: application/json header -- with it Shopify ignores
       ?view= and returns its standard product JSON (no stock). */
    var req = fetch('/products/' + encodeURIComponent(handle) + '?view=stock-data&_=' + Date.now())
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) {
        if (!t) return null;
        var parsed = JSON.parse(t);
        if (!Array.isArray(parsed.variants) || parsed.product) return null; /* not the stock view: leave lines alone */
        /* { ring, stock: {id: n}, sizes: {id: [..]} } -- rings: one piece per ticked size */
        var map = { ring: !!parsed.ring, stock: {}, sizes: {} };
        parsed.variants.forEach(function (v) {
          map.stock[v.id] = Number(v.stock) || 0;
          /* More sizes ticked than stock = a sold size not unticked yet: none is reliable. */
          var held = Array.isArray(v.sizes) ? v.sizes : [];
          map.sizes[v.id] = held.length <= map.stock[v.id] ? held : [];
        });
        return map;
      })
      .catch(function () { return null; });
    var timeout = new Promise(function (res) { setTimeout(function () { res(null); }, 2000); });
    return Promise.race([req, timeout]);
  }

  var running = false;
  /* Resolves true when it changed the cart. */
  function recheck(cart) {
    try {
      var dated = ((cart && cart.items) || []).filter(function (i) { return i.properties && i.properties[DELIVERY]; });
      if (running || !dated.length) return Promise.resolve(false);
      running = true;
      var handles = dated.map(function (i) { return i.handle; }).filter(function (h, n, a) { return h && a.indexOf(h) === n; });
      return Promise.all(handles.map(function (h) { return liveStock(h); })).then(function (maps) {
        var stockByHandle = {};
        handles.forEach(function (h, n) { stockByHandle[h] = maps[n]; });
        /* Oldest line first (cart lists newest first) gets the stock. */
        var used = {}, usedSize = {}, toFix = [];
        cart.items.slice().reverse().forEach(function (i) {
          if (!(i.properties && i.properties[DELIVERY])) return;
          var map = stockByHandle[i.handle];
          if (!map) return; /* couldn't read: leave as is */
          var have = map.stock[i.variant_id] || 0, u = used[i.variant_id] || 0;
          var ok = u + i.quantity <= have;
          if (ok && map.ring) {
            /* Rings: that size must be ticked, one piece per size. */
            var size = i.properties['Ring Size'] || '', sizeKey = i.variant_id + '|' + size;
            ok = !!size && i.quantity === 1 && (map.sizes[i.variant_id] || []).indexOf(size) !== -1 && !usedSize[sizeKey];
            if (ok) usedSize[sizeKey] = true;
          }
          if (ok) used[i.variant_id] = u + i.quantity;
          else toFix.push(i);
        });
        if (!toFix.length) { running = false; return false; }
        return toFix.reduce(function (p, i) {
          return p.then(function () {
            var props = {};
            Object.keys(i.properties).forEach(function (k) { if (k !== DELIVERY) props[k] = i.properties[k]; });
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: i.key, quantity: i.quantity, properties: props })
            }).catch(function () {});
          });
        }, Promise.resolve()).then(function () {
          running = false;
          if (window.VerasahCart) window.VerasahCart.refresh();
          return true;
        });
      }).catch(function () { running = false; return false; });
    } catch (e) { running = false; return Promise.resolve(false); }
  }

  /* Latest cart seen; lets Checkout skip the check entirely when no line
     carries a Ready-to-ship date (almost every order). */
  var lastCart = null;
  function hasDated(cart) {
    return ((cart && cart.items) || []).some(function (i) { return i.properties && i.properties[DELIVERY]; });
  }

  /* Every cart change re-renders the drawer/cart page via this event. */
  document.addEventListener('verasah:cart', function (e) { lastCart = e.detail; recheck(e.detail); });

  /* Cart page loads server-rendered: check it once. */
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById('cart-page-checkout')) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; return recheck(cart); }).catch(function () {});
  });

  /* Checkout: one last check (max ~2.5 s) only when the cart has a
     Ready-to-ship line, then always continue. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('#cart-page-checkout');
    if (!btn || btn.dataset.stockChecked === '1') return;
    if (lastCart && !hasDated(lastCart)) return; /* nothing to check: no delay */
    e.preventDefault();
    btn.dataset.stockChecked = '1';
    var go = function () { window.location.href = btn.getAttribute('href') || '/checkout'; };
    var fallback = setTimeout(go, 2500);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(recheck)
      .catch(function () {})
      .then(function () { clearTimeout(fallback); go(); });
  });
})();
